/**
 * The one editor-popup anatomy (UI spec §3.11).
 *
 * Every popup that records something — the two Manpower editors, the master data
 * record editor, the role-assignment editor, the deactivation prompt — is the same
 * shape, so it is one component rather than five copies of the same class strings:
 *
 * ```
 * ┌ card (1px border, radius, 16px pad) ────────────┐
 * │ heading           13px/600                      │
 * │ note              11px muted            optional│
 * │ ── children: findings, fields, actions ──        │
 * └─────────────────────────────────────────────────┘
 * ```
 *
 * ## Why the dialog draws nothing
 *
 * The card is the visible box. The dialog primitive contributes the modal
 * behaviour — focus trap, focus restore, `Esc`, scrim — and none of the border,
 * background or padding it ships by default, because two nested surfaces read as a
 * box drawn inside a box and each one repeats the heading. That is §3.1's
 * one-surface rule, and it is enforced here so a caller cannot get it wrong.
 *
 * `rounded-xl` on the content matches the card's own radius, so the elevation
 * shadow the modal keeps hugs the card rather than a slightly different rectangle.
 *
 * ## Why the title is in the DOM twice
 *
 * Radix names a dialog from `DialogTitle` and warns when it is absent; it resolves
 * the name from its own context id, so pointing `aria-labelledby` at the visible
 * heading instead does not satisfy it. The title and note are therefore rendered
 * once visually hidden (the accessible name and description) and once as the card's
 * heading and note. Deleting the hidden pair leaves the dialog unnamed.
 *
 * ## `autoFocusHeading`
 *
 * Off by default, which lands focus on the first control as usual. On, focus goes
 * to the heading instead: where a popup can open on several different subjects —
 * "add", "new version of…", "edit…" — the heading is the part a reader who just
 * clicked a row action needs announced before the fields. The heading then carries
 * `tabIndex={-1}` so it is a script-driven target and never a tab stop of its own.
 */
import { useId, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export interface EditorDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The card's heading, and the dialog's accessible name. */
  readonly title: string;
  /**
   * The 11px muted line under the heading, and the dialog's accessible
   * description. Omitted where the per-field hints carry the guidance instead.
   */
  readonly note?: string;
  /**
   * The card's width utility, e.g. `max-w-[560px]`. A popup is sized by what it
   * holds, so this is the caller's call rather than a fixed value.
   */
  readonly width?: string;
  /** Focus the heading rather than the first control on open — see the module note. */
  readonly autoFocusHeading?: boolean;
  /**
   * Restores keyboard focus to the control that opened the popup (R10.13, R18.6).
   * Needed because these are controlled dialogs with no in-tree `DialogTrigger`,
   * and Radix's own restore only fires for one of those; without it the closing
   * focus falls back to `<body>`.
   */
  readonly onCloseAutoFocus?: (event: Event) => void;
  readonly children: React.ReactNode;
}

export function EditorDialog({
  open,
  onOpenChange,
  title,
  note,
  width = 'max-w-[520px]',
  autoFocusHeading = false,
  onCloseAutoFocus,
  children,
}: EditorDialogProps): React.JSX.Element {
  const headingId = useId();
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn('gap-0 rounded-xl border-0 bg-transparent p-0', width)}
        {...(onCloseAutoFocus ? { onCloseAutoFocus } : {})}
        {...(autoFocusHeading && {
          onOpenAutoFocus: (event: Event) => {
            // Radix's default is the first focusable child; the heading is the
            // deliberate target here, so the default is prevented rather than
            // raced with a mount effect.
            event.preventDefault();
            headingRef.current?.focus();
          },
        })}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{title}</DialogTitle>
          {note === undefined ? null : <DialogDescription>{note}</DialogDescription>}
        </DialogHeader>

        <Card className="gap-3 p-4">
          <section aria-labelledby={headingId} className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <h3
                id={headingId}
                ref={headingRef}
                {...(autoFocusHeading && { tabIndex: -1 })}
                className="text-txt text-[13px] font-semibold outline-none"
              >
                {title}
              </h3>
              {note === undefined ? null : (
                <p className="text-muted-foreground text-[11px]">{note}</p>
              )}
            </div>

            {children}
          </section>
        </Card>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The action row of an {@link EditorDialog}: inside the card, left-aligned, and
 * **primary first**.
 *
 * Not a right-aligned footer. The row reads in the order the reader decides in —
 * the action they came to perform, then the way out — and it wraps rather than
 * compressing when a label is long.
 */
export function EditorDialogActions({
  className,
  children,
}: {
  readonly className?: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return <div className={cn('flex flex-wrap items-center gap-2', className)}>{children}</div>;
}
